import { useCallback, useEffect, useMemo } from "react";
import DadosBasicosDefaultForm from "./DadosBasicosDefaultForm";
import { useDispatch, useSelector } from "react-redux";
import useCompleteForm from "../../../../hooks/useCompleteForm";
import validators from "../../../../utils/validators";
import { actions } from "../../../../actions/clientes.actions";
import {
  removeNonDigitsFromString,
  transformToDate,
} from "../../../../utils/basic";
import { actions as parceiroActions } from "../../../../actions/parceiro.actions";
import formatters from "../../../../utils/formatters";
import {
  getPreenchimentoCliente,
  isEnviandoPreenchimentoCliente,
  isGravandoPreenchimentoCliente,
} from "../../../../selectors/clientes.selectors";

const DadosBasicosDefaultFormConnected = ({
  handleGoTo2,
  events,
  ...props
}) => {
  const dispatch = useDispatch();
  const {
    cliente: clienteData,
    dominios: {
      nacionalidade: nacionalidades,
      genero: generos,
      escolaridade: escolaridades,
      documentoPessoal: documentosPessoais,
    },
    validacao,
  } = useSelector(getPreenchimentoCliente);
  const submitting = useSelector(isGravandoPreenchimentoCliente);

  const cliente = useMemo(
    () => ({
      ...clienteData,
      fluxo: validacao.completa.cliente,
    }),
    [clienteData, validacao]
  );

  const callback = useCallback(() => {
    handleGoTo2();
  }, [handleGoTo2]);

  const [formProps, handleSubmit] = useCompleteForm({
    rules: () => ({
      cpfCnpj: validators.cpfCnpj({ required: true }),
      nome: validators.string({
        required: true,
        maxLength: 100,
        custom: {
          nomeCompleto: (value) =>
            !value ||
            (value.indexOf(" ") > 0 &&
              value.substring(value.indexOf(" ") + 1).length > 0) ||
            "Nome informado deve ser completo",
        },
      }),
      dataNascimento: validators.date({
        required: true,
        min: "01/01/1900",
        max: formatters.dates.short(new Date()),
      }),
      genero: validators.object({ required: true }),
      nacionalidade: validators.object({ required: true }),
      naturalidade: validators.string({ required: true }),
      escolaridade: validators.object({ required: true }),
      email: validators.email({ required: true }),
      nomeMae: validators.string({ required: true }),
      telefone: validators.phone({ required: true }),
      documentoPessoal: validators.object({ required: true }),
      numeroDocumentoPessoal: validators.string({
        required: true,
        maxLength: 100,
      }),
      orgaoDocumentoPessoal: validators.string({
        required: true,
        maxLength: 100,
      }),
      ufOrgaoDocumentoPessoal: validators.string({
        required: true,
      }),
      dataExpedicaoDocumentoPessoal: validators.date({ required: true }),
      endereco: {
        cep: validators.string({ required: true }),
        logradouro: validators.string({ required: true, maxLength: 40 }),
        numero: validators.string({ required: true, maxLength: 7 }),
        bairro: validators.string({ required: true, maxLength: 40 }),
        municipio: validators.string({ required: true, maxLength: 60 }),
        uf: validators.string({ required: true }),
      },
    }),
    initialValues: useCallback(
      () => ({
        cpfCnpj: "",
        nome: "",
        dataNascimento: "",
        nacionalidade: null,
        email: "",
        telefone: "",
        ...cliente,
        endereco: {
          cep: "",
          logradouro: "",
          numero: "",
          bairro: "",
          municipio: "",
          uf: "",
          complemento: "",
          ...cliente?.endereco,
        },
      }),
      [cliente]
    ),
    handleSubmit: useCallback(
      (values) => {
        dispatch(
          actions.gravarPreenchimento.request({
            callback,
            passo: "dados-pessoais",
            cliente: {
              id: cliente?.id,
              nomeCliente: values.nome,
              dataNascimento: transformToDate(values.dataNascimento),
              idGenero: values.genero?.id,
              naturalidade: values.naturalidade,
              idNacionalidade: values.nacionalidade?.id,
              idEscolaridade: values.escolaridade?.id,
              email: values.email,
              nomeMae: values.nomeMae,
              telefone: values.telefone,
              telefoneResidencial: values.telefoneResidencial,
              idDocumentoPessoal: values.documentoPessoal?.id,
              numeroDocumentoPessoal: values.numeroDocumentoPessoal,
              orgaoDocumentoPessoal: values.orgaoDocumentoPessoal,
              ufOrgaoDocumentoPessoal:
                values.documentoPessoal?.descricao === "RNE"
                  ? null
                  : values.ufOrgaoDocumentoPessoal,
              dataExpedicaoDocumentoPessoal: transformToDate(
                values.dataExpedicaoDocumentoPessoal
              ),
              endereco: {
                cep: removeNonDigitsFromString(values.endereco?.cep),
                logradouro: values.endereco.logradouro,
                numeroLogradouro: values.endereco.numero,
                complementoLogradouro: values.endereco.complemento,
                bairro: values.endereco.bairro,
                municipio: values.endereco.municipio,
                uf: values.endereco.uf,
              },
            },
          })
        );
      },
      [dispatch, cliente, callback]
    ),
  });
  const { setValue } = formProps;

  const successZipCodeCallback = useCallback(
    ({ data }) => {
      setValue("endereco.logradouro", data?.logradouro || "", {
        shouldValidate: true,
      });
      setValue("endereco.bairro", data?.bairro || "", { shouldValidate: true });
      setValue("endereco.uf", data?.uf || "", { shouldValidate: true });
      setValue("endereco.municipio", data?.localidade || "", {
        shouldValidate: true,
      });
    },
    [setValue]
  );
  const handleZipCodeBlurred = useCallback(
    (value) => {
      if (removeNonDigitsFromString(value).length === 8) {
        dispatch(
          parceiroActions.pesquisarCep.request({
            value,
            successCallback: successZipCodeCallback,
          })
        );
      }
    },
    [dispatch, successZipCodeCallback]
  );

  const { reset } = formProps;

  useEffect(() => {
    reset({
      cpfCnpj: "",
      nome: "",
      dataNascimento: "",
      nacionalidade: null,
      email: "",
      telefone: "",
      ...cliente,
      endereco: {
        cep: "",
        logradouro: "",
        bairro: "",
        municipio: "",
        numero: "",
        complemento: "",
        uf: "",
        ...cliente?.endereco,
      },
    });
  }, [cliente, reset]);

  return (
    <DadosBasicosDefaultForm
      {...props}
      tipoPessoa={cliente.cpfCnpj.length <= 11 ? "F" : "J"}
      nacionalidades={nacionalidades}
      formProps={formProps}
      handleZipCodeBlurred={handleZipCodeBlurred}
      handleSubmit={handleSubmit}
      submitting={submitting}
      fluxo={cliente.fluxo}
      events={events}
      generos={generos}
      escolaridades={escolaridades}
      documentosPessoais={documentosPessoais}
    />
  );
};

export default DadosBasicosDefaultFormConnected;
